import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useGetSquaresQuery } from '@/api/squares/getSquares';
import { useGetTenantsQuery } from '@/api/tenants/getTenants';
import { useDeleteSquareMutation } from '@/api/squares/deleteSquare';
import { getSportLogoUrl } from '@low6-pickem/sports-config';
import { Plus, Settings, MoreVertical, Pencil, Trash2, Calendar } from 'lucide-react';
import { OpenPublicUrlMenuItem } from '@/components/dashboard/OpenPublicUrlMenuItem';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSearch, useNavigate, Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCurrentAdminUser } from '@/hooks/useCurrentAdminUser';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

export const Route = createFileRoute('/dashboard/squares/')({
  beforeLoad: ({ context }) => {
    const { allowedGameTypes } = context.adminUser;
    if (allowedGameTypes !== null && !allowedGameTypes.includes('squares')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: SquaresPage,
});

interface SquareData {
  squares_contest_id: string;
  friendly_id?: number;
  tenant_id?: string;
  contest_name_en?: string;
  contest_name_fr?: string;
  start_time_utc?: string;
  status?: string;
  sport?: string;
  total_entries_to_date?: number;
  total_prize_amount?: number;
  periods?: Array<{ prizeAmount?: number; prize_amount?: number }>;
  id: string;
  label: string;
}

/** Prize amount to show: use total_prize_amount or sum of periods (backend may send 0 for legacy list). */
function getDisplayPrizeAmount(square: SquareData): number {
  const total = square.total_prize_amount;
  if (total != null && total > 0) return total;
  const periods = square.periods || [];
  return periods.reduce((sum, p) => {
    const raw = p.prizeAmount ?? p.prize_amount ?? 0;
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw || '0'));
    return sum + (Number.isNaN(n) ? 0 : n);
  }, 0);
}

function formatPrizeAmount(amount: number): string {
  return `$${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

function SquaresPage() {
  const { canManageContests } = useCurrentAdminUser();
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/dashboard/squares/' }) as { tenant_id?: string };
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [leagueFilter] = useState<string | undefined>(searchParams.tenant_id);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [squareToDelete, setSquareToDelete] = useState<SquareData | null>(null);

  const { data: tenants = [] } = useGetTenantsQuery();
  const { data: squares = [], isLoading } = useGetSquaresQuery({
    status: statusFilter !== 'all' ? statusFilter : undefined,
  });
  const deleteSquareMutation = useDeleteSquareMutation();

  const formattedSquares = (squares || [])
    .filter(square => {
      if (leagueFilter && square.tenant_id !== leagueFilter) return false;
      if (tenantFilter !== 'all' && square.tenant_id !== tenantFilter) return false;
      if (startDate && square.start_time_utc) {
        if (new Date(square.start_time_utc) < new Date(startDate)) return false;
      }
      if (endDate && square.start_time_utc) {
        if (new Date(square.start_time_utc) > new Date(endDate)) return false;
      }
      return true;
    })
    .map((square, index) => ({
      ...square,
      id: square.squares_contest_id ? square.squares_contest_id.toString() : `temp-${index}`,
      label: square.contest_name_en || square.contest_name_fr || 'Untitled',
    }));

  const handleEditSquare = (square: SquareData) => {
    navigate({
      to: '/dashboard/squares/$id',
      params: { id: square.id },
      search: { tenant_id: undefined, tab: undefined },
    });
  };

  const handleCreateSquare = () => {
    navigate({
      to: '/dashboard/squares/$id',
      params: { id: 'new' },
      search: { tenant_id: leagueFilter, tab: undefined },
    });
  };

  const handleDeleteClick = (square: SquareData) => {
    setSquareToDelete(square);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (squareToDelete) {
      await deleteSquareMutation.mutateAsync(squareToDelete.id);
      setDeleteDialogOpen(false);
      setSquareToDelete(null);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return 'bg-gray-500';
      case 'published':
      case 'open':
        return 'bg-blue-500';
      case 'locked':
      case 'live':
        return 'bg-green-500';
      case 'final':
        return 'bg-purple-500';
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <TooltipProvider>
      <PageContainer>
        <Breadcrumbs overrides={{ squares: 'Squares' }} />
        <div className="flex gap-4 items-center mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-gray-500" />
            <h1 className="text-2xl font-semibold">Squares</h1>
          </div>

          <Select value={tenantFilter} onValueChange={setTenantFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Tenants" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tenants</SelectItem>
              {tenants.map(tenant => (
                <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>
                  {tenant.name_en || tenant.name_fr || tenant.title || 'Unnamed Tenant'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="locked">Locked</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="final">Final</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative">
            <Input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="w-[180px] pr-8"
              placeholder="Start Date"
            />
            <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative">
            <Input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="w-[180px] pr-8"
              placeholder="End Date"
            />
            <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/dashboard/squares/settings" search={{ tab: 'game-rules' }}>
                  <Button variant="outline" size="icon">
                    <Settings className="h-4 w-4" />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent>Settings</TooltipContent>
            </Tooltip>
            {canManageContests && (
              <Button onClick={handleCreateSquare}>
                <Plus className="mr-2 h-4 w-4" />
                Create Contest
              </Button>
            )}
          </div>
        </div>

        <ContentCard>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : formattedSquares.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No squares contests found</p>
              {canManageContests && (
                <Button onClick={handleCreateSquare}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Your First Squares Contest
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader className="[&_tr]:border-b-0">
                <TableRow>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold w-16">
                    Sport
                  </TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">ID</TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                    Contest Name
                  </TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                    Start Time
                  </TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Entries</TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                    Prize Amount
                  </TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Status</TableHead>
                  <TableHead className="text-right bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formattedSquares.map((square: SquareData) => (
                  <TableRow
                    key={square.id}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-accent"
                    onClick={() => handleEditSquare(square)}
                  >
                    <TableCell>
                      <img
                        src={getSportLogoUrl(square.sport)}
                        alt={square.sport || 'Sport'}
                        className="h-6 w-6 object-contain"
                      />
                    </TableCell>
                    <TableCell className="font-medium font-mono">
                      {square.friendly_id ?? '-'}
                    </TableCell>
                    <TableCell className="font-medium">
                      {square.contest_name_en || square.contest_name_fr || 'Untitled'}
                    </TableCell>
                    <TableCell>
                      {square.start_time_utc ? (
                        <TzDateDisplay
                          utcDate={square.start_time_utc}
                          formatString="MMM d, yyyy h:mm a"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{square.total_entries_to_date || 0}</TableCell>
                    <TableCell>{formatPrizeAmount(getDisplayPrizeAmount(square))}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(square.status)}>
                        {square.status || 'draft'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditSquare(square)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <OpenPublicUrlMenuItem
                            gameType="squares"
                            tenantId={square.tenant_id}
                            gameId={square.squares_contest_id}
                          />
                          {canManageContests && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDeleteClick(square)}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ContentCard>
      </PageContainer>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Squares Contest</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{squareToDelete?.label}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
